import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

import { groupedTableControls } from './tableControls';

// Wraps wide content (e.g. a data table) in a horizontal scroll area, fading the
// overflowing edge so it's clear there's more to scroll to. With `pinnedControls`,
// the scroll area's direct `<div>` children (a table's control rows) stick in place
// while only the `<table>` scrolls.

const Wrapper = styled.div`
  position: relative;
`;

const ScrollArea = styled.div`
  overflow-x: auto;

  &[data-pinned-controls='true'] {
    ${groupedTableControls}
  }

  &[data-pinned-controls='true'] > div {
    position: sticky;
    left: 0;
    z-index: 1;
  }
`;

const Edge = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 56px;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const RightEdge = styled(Edge)`
  right: 0;
  justify-content: flex-end;
  background: linear-gradient(to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.16));
`;

const LeftEdge = styled(Edge)`
  left: 0;
  background: linear-gradient(to left, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.16));
`;

const Chevron = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-right: 4px;
  border-radius: 50%;
  color: white;
  background-color: rgba(0, 0, 0, 0.45);
`;

const HorizontalScroll = ({ children, pinnedControls }) => {
  const scrollRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const element = scrollRef.current;
    if (!element) { return; }

    const hasOverflow = element.scrollWidth > element.clientWidth + 1;
    const atStart = element.scrollLeft <= 0;
    const atEnd =
      element.scrollLeft + element.clientWidth >= element.scrollWidth - 1;

    setEdges({
      left: hasOverflow && !atStart,
      right: hasOverflow && !atEnd,
    });
  }, []);

  useEffect(() => {
    update();

    const element = scrollRef.current;
    let observer;
    if (typeof ResizeObserver !== 'undefined' && element) {
      observer = new ResizeObserver(update);
      observer.observe(element);
      if (element.firstElementChild) {
        observer.observe(element.firstElementChild);
      }
    }
    window.addEventListener('resize', update);

    return () => {
      window.removeEventListener('resize', update);
      if (observer) { observer.disconnect(); }
    };
  }, [update]);

  return (
    <Wrapper>
      <ScrollArea
        ref={scrollRef}
        data-testid="horizontal-scroll"
        data-pinned-controls={pinnedControls ? 'true' : undefined}
        onScroll={update}
      >
        {children}
      </ScrollArea>
      {edges.left && <LeftEdge data-testid="scroll-hint-left" />}
      {edges.right && (
        <RightEdge data-testid="scroll-hint-right">
          <Chevron>
            <FontAwesomeIcon icon={faChevronRight} />
          </Chevron>
        </RightEdge>
      )}
    </Wrapper>
  );
};

export default HorizontalScroll;
